<template>
  <div>
    <v-card v-if="isDev" class="mb-4" outlined>
      <v-card-title>Dev Tools</v-card-title>
      <v-card-text>
        <v-switch
          v-model="useDummyData"
          :disabled="dummyLoading"
          label="Use Dummy Booking Data"
          @change="toggleDummyData"
        ></v-switch>
        <v-progress-linear
          v-if="dummyLoading"
          indeterminate
          color="primary"
        ></v-progress-linear>
      </v-card-text>
    </v-card>
    <booking-presentation
      ref="bookingPresentation"
      :data="data.img"
    ></booking-presentation>
    <v-row>
      <InvoiceHeader></InvoiceHeader>
      <v-card flat width="100%">
        <v-card-title>Client</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-text-field
                outlined
                v-model="data.client_name"
                label="Client Name"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                outlined
                v-model="data.business_name"
                label="Business Name"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                outlined
                v-model="data.website"
                label="Website"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                outlined
                v-model="data.abn"
                label="ABN"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card flat width="100%">
        <v-card-title>Address</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="4">
              <v-text-field
                outlined
                v-model="data.unit"
                label="Unit no"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                outlined
                v-model="data.street"
                label="Street name"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <select-suburbs outlined v-model="data.suburb"></select-suburbs>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-card flat width="100%">
        <v-card-title>Contact</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="4">
              <v-text-field
                outlined
                v-model="data.telephone"
                label="Telephone"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                outlined
                v-model="data.mobile"
                label="Mobile"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                outlined
                v-model="data.email"
                label="Email"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-card flat width="100%">
        <v-card-title>Contract</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <date-picker
                outlined
                label="Sale Date"
                v-model="data.sale_date"
              ></date-picker>
            </v-col>
            <v-col cols="6">
              <date-picker
                outlined
                label="Start Date"
                v-model="data.start_date"
              ></date-picker>
            </v-col>
            <v-col cols="6">
              <date-picker
                outlined
                label="End Date"
                v-model="data.end_date"
              ></date-picker>
            </v-col>
            <v-col cols="12">
              <select-multi-suburbs
                outlined
                v-model="data.suburbs"
              ></select-multi-suburbs>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <select-multi-categories
                outlined
                v-model="localState.categories"
              ></select-multi-categories>
            </v-col>

            <v-col cols="12" md="6" v-if="showVariationBuilder">
              <select-suburbs
                outlined
                label="Anchor Suburb"
                v-model="localState.anchorSuburb"
              ></select-suburbs>
            </v-col>

            <v-col cols="12" md="6" v-if="showVariationBuilder">
              <v-select
                outlined
                label="Display Size"
                :items="sizeOptions"
                v-model="localState.size"
              ></v-select>
            </v-col>

            <v-col cols="12" md="6" v-if="showVariationBuilder">
              <v-select
                outlined
                label="Radius"
                :items="radiusOptions"
                item-text="text"
                item-value="value"
                v-model="localState.radiusLevel"
              ></v-select>
            </v-col>

            <v-col cols="12" v-if="showVariationBuilder">
              <coverage-preview
                :anchor-suburb="localState.anchorSuburb"
                :radius-level="localState.radiusLevel"
              ></coverage-preview>
            </v-col>

            <v-col cols="12" v-if="showVariationBuilder">
              <v-btn :disabled="!canSaveCoverage" @click="saveRadiusCoverage">
                {{
                  editingIndex !== null ? 'Update variation' : 'Save variation'
                }}
              </v-btn>
            </v-col>

            <v-col v-if="showLegacyCombinationsNotice" cols="12">
              <v-alert dense outlined type="warning">
                This invoice still contains legacy region combinations. Add a
                radius variation to move it to the new coverage model.
              </v-alert>
            </v-col>

            <v-col cols="12" v-if="radiusCoverages.length">
              <v-card
                v-for="(coverage, index) in radiusCoverages"
                :key="`coverage-${index}`"
                class="mb-3"
                outlined
              >
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="4">
                      <strong>Categories:</strong>
                      <div
                        v-for="(category, categoryIndex) in coverage.categories"
                        :key="`coverage-${index}-category-${categoryIndex}`"
                      >
                        {{ category }}
                      </div>
                    </v-col>

                    <v-col cols="12" md="4">
                      <strong>Anchor Suburb:</strong>
                      <div>{{ coverage.anchor_suburb }}</div>
                    </v-col>

                    <v-col cols="12" md="2">
                      <strong>Size:</strong>
                      <div>{{ coverage.size }}</div>
                    </v-col>

                    <v-col cols="12" md="2">
                      <strong>Radius:</strong>
                      <div>{{ radiusLabelForCoverage(coverage) }}</div>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    small
                    text
                    color="primary"
                    @click="
                      previewCoverageIndex =
                        previewCoverageIndex === index ? null : index
                    "
                  >
                    <v-icon small left>mdi-map-outline</v-icon>
                    {{
                      previewCoverageIndex === index
                        ? 'Hide coverage'
                        : 'View coverage'
                    }}
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn icon color="blue" @click="editRadiusCoverage(index)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon color="red" @click="deleteRadiusCoverage(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-card-actions>
                <v-card-text v-if="previewCoverageIndex === index">
                  <coverage-preview
                    :anchor-suburb="coverage.anchor_suburb"
                    :radius-level="
                      normalizeRadiusLevel(
                        coverage.radius_level !== undefined
                          ? coverage.radius_level
                          : coverage.radius
                      )
                    "
                  ></coverage-preview>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-col cols="12">
        <v-row>
          <v-col cols="6">
            <v-card flat width="100%">
              <v-card-title>Proof</v-card-title>
              <v-card-text>
                <v-select
                  outlined
                  :items="[
                    'Business Card',
                    'Signed Copy',
                    'As Pervious Edition'
                  ]"
                  label="Select type of proof"
                  v-model="data.proof"
                ></v-select>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="6">
            <v-card flat width="100%">
              <v-card-title>Offer</v-card-title>
              <v-card-text>
                <v-text-field
                  outlined
                  label="Offer Detail"
                  v-model="data.offer"
                ></v-text-field>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <v-card flat width="100%">
        <v-card-title>Renewal</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="4">
              <v-switch label="renewal"></v-switch>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="data.last_edition_size"
                outlined
                label="Last Edition Size"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="data.last_edition_cost"
                outlined
                label="Last Edition Price"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-col cols="12">
        <select-employee outlined v-model="data.employee_id"></select-employee>
      </v-col>
      <v-card flat width="100%">
        <v-card-title>Payment Method</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="4">
              <v-text-field label="Sub Total" outlined v-model="data.sub_total">
              </v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                label="GST"
                outlined
                v-model="data.gst"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="data.total"
                outlined
                label="Total"
              ></v-text-field>
            </v-col>

            <v-col cols="6"
              ><v-text-field
                label="Deposit"
                outlined
                v-model="data.deposit"
              ></v-text-field
            ></v-col>
            <v-col cols="6"
              ><v-text-field
                label="Balance"
                outlined
                v-model="data.balance"
              ></v-text-field
            ></v-col>

            <v-col cols="6"
              ><v-select
                outlined
                label="Payment type"
                v-model="data.payment_type"
                :items="['Upfront', 'Installments', 'Pub']"
              ></v-select
            ></v-col>
            <v-col cols="6"
              ><month-picker
                outlined
                label="Month Beg."
                v-model="data.month_beginning"
            /></v-col>
            <v-col cols="6"
              ><v-text-field
                v-model="data.total_months"
                label="Total Months"
                outlined
              ></v-text-field
            ></v-col>
            <v-col cols="6"
              ><v-text-field
                label="Monthly Amount"
                v-model="data.monthly_amount"
                outlined
              ></v-text-field
            ></v-col>
          </v-row>
          <v-row>
            <v-tabs v-model="tab" grow>
              <v-tabs-slider></v-tabs-slider>
              <v-tab href="#card">
                <v-icon left>mdi-credit-card</v-icon>Card
              </v-tab>
              <v-tab href="#direct-debit">
                <v-icon left>mdi-bank</v-icon>Direct Debit
              </v-tab>

              <v-tab-item value="card">
                <v-card flat tile>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          outlined
                          label="Name on Card"
                          v-model="data.name_on_card"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="data.card_number"
                          outlined
                          label="Card Number"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="2">
                        <v-text-field
                          v-model="data.expiry_month"
                          outlined
                          label="Expiry Month"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="2">
                        <v-text-field
                          outlined
                          v-model="data.expiry_year"
                          label="Expiry Year"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="2">
                        <v-text-field
                          v-model="data.cvv"
                          outlined
                          label="CVV"
                          type="password"
                          autocomplete="new-password"
                          inputmode="numeric"
                          maxlength="4"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>
              <v-tab-item value="direct-debit">
                <v-card flat tile>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          outlined
                          v-model="data.account_name"
                          label="Account Name"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          outlined
                          v-model="data.bsb_number"
                          label="BSB Number"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          outlined
                          v-model="data.account_number"
                          label="Account Number"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs>
            <!-- <iframe
            src="https://widget.ezidebit.com.au/account/edit?dk=7655AC52BO35624CFED203CD3DC84397&cr=100-216-772"
            width="600"
            height="400"
            frameborder="0"
            allowtransparency="true"
          ></iframe> -->
          </v-row>
        </v-card-text>
      </v-card>

      <v-col cols="12">
        <v-textarea outlined label="Notes" v-model="data.notes"></v-textarea>
      </v-col>

      <v-card flat width="100%">
        <v-card-title>Signature</v-card-title>
        <v-card-text>
          <v-row>
            <div class="elevation-10 pa-4 mx-auto" style="border-radius:10px;">
              <vueSignature
                :sigOption="sigOption"
                v-model="data.signature"
                ref="signature"
                w="300px"
                h="200px"
              ></vueSignature>
            </div>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="() => $refs.signature.undo()">
            <v-icon>mdi-undo-variant</v-icon>
          </v-btn>
          <v-btn @click="_clearSignature">
            <v-icon>mdi-cached</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
      <InvoiceFooter></InvoiceFooter>
    </v-row>
  </div>
</template>

<script>
import MonthPicker from '../../../components/MonthPicker'
import SelectMultiCategories from '../../../components/SelectMultiCategories'
import SelectSuburbs from '../../../components/SelectSuburbs'
import SelectMultiSuburbs from '../../../components/SelectMultiSuburbs'
// import SelectRegions from '../../../components/SelectRegions'
import DatePicker from '../../../components/DatePicker'
import { axios } from '../../../utils'
import vueSignature from 'vue-signature'
import InvoiceHeader from './InvoiceHeader'
import InvoiceFooter from './InvoiceFooter'
import SelectEmployee from '../../../components/SelectEmployee'
export default {
  components: {
    SelectEmployee,
    MonthPicker,
    SelectMultiCategories,
    SelectMultiSuburbs,
    SelectSuburbs,
    // SelectRegions,
    DatePicker,
    vueSignature,
    InvoiceHeader,
    InvoiceFooter,
    CoveragePreview: () => import('./CoveragePreview'),
    BookingPresentation: () => import('./BookingPresentation')
  },
  name: 'BookingForm',
  props: ['data', 'saveSignature', 'clearSignature'],
  data() {
    return {
      useDummyData: false,
      dummyLoading: false,
      localState: {
        categories: [],
        anchorSuburb: '',
        size: '',
        radiusLevel: null
      },
      radiusCoverages: this.data.radius_coverages || [],
      editingIndex: null,
      previewCoverageIndex: null,
      sizeOptions: ['A', 'B', 'C', 'D'],
      radiusOptions: [
        { text: '10 km tier (100 suburbs)', value: 1 },
        { text: '20 km tier (200 suburbs)', value: 2 },
        { text: '30 km tier (300 suburbs)', value: 3 },
        { text: '40 km tier (395 suburbs)', value: 4 }
      ],
      tab: 0,
      sigOption: {
        penColor: 'rgb(0, 0, 0)',
        backgroundColor: '#fff'
      }
    }
  },
  computed: {
    isDev() {
      return process.env.NODE_ENV === 'development'
    },
    showVariationBuilder() {
      return this.localState.categories.length > 0
    },
    canSaveCoverage() {
      return (
        this.localState.categories.length > 0 &&
        !!this.localState.anchorSuburb &&
        !!this.localState.size &&
        this.localState.radiusLevel !== null
      )
    },
    showLegacyCombinationsNotice() {
      return (
        !this.radiusCoverages.length &&
        Array.isArray(this.data.combinations) &&
        this.data.combinations.length > 0
      )
    }
  },
  methods: {
    saveRadiusCoverage() {
      if (!this.canSaveCoverage) {
        return
      }

      const coverage = {
        categories: [...this.localState.categories],
        anchor_suburb: this.localState.anchorSuburb,
        size: this.localState.size,
        radius_level: this.localState.radiusLevel
      }

      if (this.editingIndex !== null && this.editingIndex !== undefined) {
        this.radiusCoverages.splice(this.editingIndex, 1, coverage)
      } else {
        this.radiusCoverages.push(coverage)
      }

      this.syncRadiusCoveragesToParent()
      this.resetCoverageBuilder()
    },
    deleteRadiusCoverage(index) {
      this.previewCoverageIndex = null
      this.radiusCoverages.splice(index, 1)
      this.syncRadiusCoveragesToParent()
      if (this.editingIndex === index) {
        this.resetCoverageBuilder()
      }
    },
    editRadiusCoverage(index) {
      const coverage = this.radiusCoverages[index]
      if (!coverage) {
        return
      }

      this.localState.categories = Array.isArray(coverage.categories)
        ? [...coverage.categories]
        : []
      this.localState.anchorSuburb = coverage.anchor_suburb || ''
      this.localState.size = coverage.size || ''
      this.localState.radiusLevel = this.normalizeRadiusLevel(
        coverage.radius_level !== undefined
          ? coverage.radius_level
          : coverage.radius
      )
      this.editingIndex = index
    },
    resetCoverageBuilder() {
      this.localState.categories = []
      this.localState.anchorSuburb = ''
      this.localState.size = ''
      this.localState.radiusLevel = null
      this.editingIndex = null
    },
    syncRadiusCoveragesToParent() {
      this.data.radius_coverages = [...this.radiusCoverages]
      if (this.radiusCoverages.length > 0) {
        this.data.combinations = []
        const anchorSuburbs = this.radiusCoverages
          .map(coverage => coverage.anchor_suburb)
          .filter(Boolean)

        if (anchorSuburbs.length > 0) {
          const mergedSuburbs = [
            ...new Set([...(this.data.suburbs || []), ...anchorSuburbs])
          ]
          this.data.suburbs = mergedSuburbs
        }
      }
      this.$emit('update:data', { ...this.data })
    },
    normalizeRadiusLevel(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }

      if (!Number.isNaN(Number(value))) {
        const numericValue = Number(value)
        if ([1, 2, 3, 4].includes(numericValue)) {
          return numericValue
        }
      }

      const normalized = `${value}`.toLowerCase().replace(/[\s-]/g, '')

      switch (normalized) {
        case '10':
        case '10km':
        case 'within10km':
        case '5':
        case '5km':
        case 'within5km':
        case '15':
        case '15km':
        case 'within15km':
          return 1
        case '20':
        case '20km':
        case 'within20km':
          return 2
        case '30':
        case '30km':
        case 'within30km':
        case '45':
        case '45km':
        case 'within45km':
          return 3
        case '40':
        case '40km':
        case 'within40km':
        case '60':
        case '60km':
        case 'within60km':
        case 'metro':
        case 'metrowide':
        case 'allmetro':
          return 4
        default:
          return null
      }
    },
    radiusLabelForLevel(radiusLevel) {
      switch (this.normalizeRadiusLevel(radiusLevel)) {
        case 1:
          return 'Within 10km'
        case 2:
          return 'Within 20km'
        case 3:
          return 'Within 30km'
        case 4:
          return 'Within 40km'
        default:
          return ''
      }
    },
    radiusLabelForCoverage(coverage) {
      return this.radiusLabelForLevel(
        coverage.radius_level !== undefined
          ? coverage.radius_level
          : coverage.radius
      )
    },
    toggleDummyData(value) {
      if (!this.isDev) {
        return
      }
      if (value) {
        this.applyDummyData()
        return
      }
      this.clearDummyData()
    },
    async applyDummyData() {
      this.dummyLoading = true
      try {
        const [
          employeesResult,
          suburbsResult,
          categoriesResult
        ] = await Promise.allSettled([
          axios.get('api/admin/select-employees'),
          axios.get('api/admin/admin-suburbs?q=a'),
          axios.get('/api/categories?q=a&portal=yes')
        ])

        const employees =
          employeesResult.status === 'fulfilled'
            ? employeesResult.value.data
            : []
        const suburbs =
          suburbsResult.status === 'fulfilled' ? suburbsResult.value.data : []
        const categories =
          categoriesResult.status === 'fulfilled'
            ? categoriesResult.value.data
            : []

        const suburbNames = this._normalizeList(suburbs, 'name')
        const categoryNames = this._normalizeList(categories, 'name')

        const firstNames = [
          'Ava',
          'Liam',
          'Mia',
          'Noah',
          'Zoe',
          'Ethan',
          'Ruby',
          'Leo'
        ]
        const lastNames = [
          'Taylor',
          'Nguyen',
          'Patel',
          'Walker',
          'Singh',
          'Brown',
          'Lopez',
          'Chen'
        ]
        const businessTypes = [
          'Plumbing',
          'Electrical',
          'Landscaping',
          'Cafe',
          'Design',
          'Cleaning',
          'Auto Care',
          'Construction'
        ]
        const streetNames = [
          'King St',
          'River Rd',
          'Station Ave',
          'Harbor Blvd',
          'Garden Way',
          'Market St'
        ]
        const leadFromOptions = [
          'Referral',
          'Online',
          'Cold Call',
          'Walk-in',
          'Partner'
        ]
        const proofOptions = [
          'Business Card',
          'Signed Copy',
          'As Pervious Edition'
        ]
        const paymentTypes = ['Upfront', 'Installments', 'Pub']
        const coverageSizes = ['A', 'B', 'C', 'D']
        const coverageRadiusLevels = [1, 2, 3, 4]

        const firstName = this._randomItem(firstNames, 'Alex')
        const lastName = this._randomItem(lastNames, 'Taylor')
        const fullName = `${firstName} ${lastName}`
        const businessType = this._randomItem(businessTypes, 'Services')
        const businessName = `${lastName} ${businessType} Pty Ltd`
        const streetName = this._randomItem(streetNames, 'Main St')
        const unitNumber = this._randomInt(1, 20)
        const streetNumber = this._randomInt(10, 999)
        const randId = this._randomInt(100, 999)

        const primarySuburb = this._randomItem(suburbNames, 'Melbourne')
        const multiSuburbs = this._randomSubset(
          suburbNames.length ? suburbNames : [primarySuburb],
          3
        )
        const selectedCategories = this._randomSubset(
          categoryNames.length ? categoryNames : ['Plumbing', 'Electricians'],
          2
        )

        const today = new Date()
        const saleDate = this._formatDate(
          this._addDays(today, this._randomInt(0, 5))
        )
        const startDate = this._formatDate(
          this._addDays(today, this._randomInt(7, 30))
        )
        const endDate = this._formatDate(
          this._addDays(today, this._randomInt(200, 400))
        )
        const monthBeginning = this._formatMonth(this._addDays(today, 30))

        const employeeId = employees.length
          ? this._randomItem(employees, employees[0]).id
          : 1

        const radiusCoverages = this._randomSubset(
          multiSuburbs.length ? multiSuburbs : [primarySuburb],
          2
        ).map(suburb => ({
          categories: selectedCategories.length
            ? [...selectedCategories]
            : ['Plumbing', 'Electricians'],
          anchor_suburb: suburb,
          size: this._randomItem(coverageSizes, 'A'),
          radius_level: this._randomItem(coverageRadiusLevels, 2)
        }))

        const subTotal = this._randomInt(800, 3000)
        const gst = Math.round(subTotal * 0.1)
        const total = subTotal + gst
        const deposit = Math.round(total * 0.2)
        const balance = total - deposit
        const totalMonths = this._randomItem([6, 12, 18], 12)
        const monthlyAmount = Math.max(1, Math.round(balance / totalMonths))

        const paymentType = this._randomItem(paymentTypes, 'Installments')

        const dummyData = {
          client_name: fullName,
          business_name: businessName,
          website: 'https://example.com',
          abn: this._formatAbn(this._randomDigits(11)),
          unit: `Suite ${unitNumber}`,
          street: `${streetNumber} ${streetName}`,
          suburb: primarySuburb,
          suburbs: multiSuburbs.length ? multiSuburbs : [primarySuburb],
          telephone: this._formatLandline(),
          mobile: this._formatMobile(),
          email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}${randId}@example.com`,
          combinations: [],
          radius_coverages: radiusCoverages,
          sale_date: saleDate,
          start_date: startDate,
          end_date: endDate,
          name_on_card: fullName,
          card_number: '4242424242424242',
          expiry_month: `${this._randomInt(1, 12)}`.padStart(2, '0'),
          expiry_year: `${this._randomInt(28, 32)}`,
          cvv: `${this._randomInt(100, 999)}`,
          account_name: businessName,
          bsb_number: `${this._randomInt(1, 999999)}`.padStart(6, '0'),
          account_number: `${this._randomInt(10000000, 99999999)}`,
          proof: this._randomItem(proofOptions, 'Signed Copy'),
          sub_total: `${subTotal}`,
          gst: `${gst}`,
          total: `${total}`,
          deposit: `${deposit}`,
          balance: `${balance}`,
          payment_type: paymentType,
          month_beginning: monthBeginning,
          total_months: `${totalMonths}`,
          monthly_amount: `${monthlyAmount}`,
          employee_id: employeeId,
          lead_from: this._randomItem(leadFromOptions, 'Referral'),
          offer: `${this._randomInt(5, 15)}% off first year`,
          last_edition_cost: `${this._randomInt(500, 1200)}`,
          last_edition_size: this._randomItem(['A', 'B', 'C', 'D'], 'B'),
          renewal: false,
          notes: `Demo booking ${randId} - dev only.`,
          price: `${total}`
        }

        Object.keys(dummyData).forEach(key => {
          this.data[key] = dummyData[key]
        })

        await this.$nextTick()

        if (
          this.$refs.bookingPresentation &&
          typeof this.$refs.bookingPresentation.applyDummyDrawing === 'function'
        ) {
          this.$refs.bookingPresentation.applyDummyDrawing({
            title: `Demo Contract ${randId}`,
            seedOffset: this._randomInt(1, 10)
          })
        }
        this._applyDummySignature(fullName)
      } catch (err) {
        console.log(err)
      } finally {
        this.dummyLoading = false
      }
    },
    clearDummyData() {
      Object.keys(this.data).forEach(key => {
        const value = this.data[key]
        if (Array.isArray(value)) {
          this.data[key] = []
          return
        }
        if (typeof value === 'boolean') {
          this.data[key] = false
          return
        }
        if (typeof value === 'number') {
          this.data[key] = null
          return
        }
        this.data[key] = ''
      })

      this.radiusCoverages = []
      this.resetCoverageBuilder()

      if (this.$refs.signature) {
        this._clearSignature()
      }

      if (
        this.$refs.bookingPresentation &&
        typeof this.$refs.bookingPresentation.clearDrawing === 'function'
      ) {
        this.$refs.bookingPresentation.clearDrawing()
      }
    },
    _normalizeList(items, key) {
      return (items || [])
        .map(item => {
          if (!item) return null
          if (typeof item === 'string') return item
          return item[key] || item.name || item.label || null
        })
        .filter(Boolean)
    },
    _formatDate(date) {
      return date.toISOString().slice(0, 10)
    },
    _formatMonth(date) {
      return date.toISOString().slice(0, 7)
    },
    _addDays(date, days) {
      const result = new Date(date)
      result.setDate(result.getDate() + days)
      return result
    },
    _applyDummySignature(name) {
      const signatureRef = this.$refs.signature
      if (!signatureRef || typeof signatureRef.fromDataURL !== 'function') {
        return
      }

      const canvas =
        signatureRef.sig && signatureRef.sig.canvas
          ? signatureRef.sig.canvas
          : null
      const width = canvas ? canvas.width : 300
      const height = canvas ? canvas.height : 200
      const dataUrl = this._buildDummySignatureDataUrl(width, height, name)

      signatureRef.clear()
      signatureRef.fromDataURL(dataUrl)

      const saved = signatureRef.save()
      if (saved) {
        this.saveSignature(saved)
      }
    },
    _buildDummySignatureDataUrl(width, height, name = 'Demo Signature') {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        return ''
      }

      const fontSize = Math.max(Math.round(width / 6), 24)
      ctx.fillStyle = '#000'
      ctx.strokeStyle = '#000'
      ctx.lineWidth = 2 + this._randomInt(0, 2)
      ctx.font = `${fontSize}px sans-serif`
      ctx.fillText(name, 10, Math.round(height * 0.6))

      ctx.beginPath()
      ctx.moveTo(10, Math.round(height * 0.7))
      ctx.bezierCurveTo(
        width * 0.3 + this._randomInt(-15, 15),
        height * 0.5 + this._randomInt(-10, 10),
        width * 0.6 + this._randomInt(-15, 15),
        height * 0.9 + this._randomInt(-10, 10),
        width * 0.9 + this._randomInt(-10, 10),
        height * 0.6 + this._randomInt(-10, 10)
      )
      ctx.stroke()

      return canvas.toDataURL('image/png')
    },
    _randomItem(items, fallback) {
      if (!items || !items.length) {
        return fallback
      }
      return items[this._randomInt(0, items.length - 1)]
    },
    _randomSubset(items, count) {
      const source = [...(items || [])]
      if (!source.length) {
        return []
      }
      const targetCount = Math.min(count, source.length)
      const result = []
      while (result.length < targetCount) {
        const idx = this._randomInt(0, source.length - 1)
        result.push(source.splice(idx, 1)[0])
      }
      return result
    },
    _randomInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min
    },
    _randomDigits(length) {
      let output = ''
      for (let i = 0; i < length; i += 1) {
        output += `${this._randomInt(0, 9)}`
      }
      return output
    },
    _formatAbn(digits) {
      if (!digits || digits.length < 11) {
        return digits
      }
      return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(
        5,
        8
      )} ${digits.slice(8, 11)}`
    },
    _formatMobile() {
      return `04${this._randomDigits(8)}`
    },
    _formatLandline() {
      return `0${this._randomItem([2, 3, 7, 8], 3)} ${this._randomDigits(
        4
      )} ${this._randomDigits(4)}`
    },
    autoCaptureArtifacts() {
      if (
        this.$refs.bookingPresentation &&
        typeof this.$refs.bookingPresentation.captureDrawing === 'function'
      ) {
        this.$refs.bookingPresentation.captureDrawing()
      }
      this._autoSaveSignature()
    },
    _autoSaveSignature() {
      const signatureRef = this.$refs.signature
      if (!signatureRef) {
        return
      }
      if (
        typeof signatureRef.isEmpty === 'function' &&
        signatureRef.isEmpty()
      ) {
        return
      }
      let svg = signatureRef.save()
      if (svg) {
        this.saveSignature(svg)
      }
    },
    _clearSignature() {
      this.$refs.signature.clear()
      this.clearSignature()
    }
  },
  watch: {
    'data.radius_coverages': {
      handler(value) {
        this.radiusCoverages = Array.isArray(value) ? value : []
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style scoped></style>
